import http from './api'

export type SocialPostStatus =
  | 'pending'
  | 'rendered'
  | 'published'
  | 'skipped'
  | 'failed'

export interface SocialPlatformResult {
  status: 'pending' | 'published' | 'skipped' | 'failed'
  postId?: string
  permalink?: string
  error?: string
  publishedAt?: string
}

export interface SocialPosterData {
  teamA?: string
  teamB?: string
  leagueLabel?: string
  dateLabel?: string
  timeLabel?: string
  totalPredictions?: number
  [key: string]: unknown
}

export interface SocialPost {
  id: string
  matchId: string
  league: string | null
  status: SocialPostStatus
  scheduledFor: string | null
  imageUrl: string | null
  posterData: SocialPosterData | null
  captions: { instagram?: string; facebook?: string; linkedin?: string } | null
  results: Record<string, SocialPlatformResult> | null
  createdAt: string
  updatedAt: string
}

export interface SocialRunResult {
  matchId: string
  teams: string
  status: string
  totalPredictions: number
  imageUrl?: string | null
  results?: Record<string, SocialPlatformResult> | null
  skippedReason?: string
}

class SocialService {
  /** Render (and optionally publish) posters for tomorrow's matches. */
  async generateTomorrow(publish = false, force = false): Promise<SocialRunResult[]> {
    const res = await http.post<SocialRunResult[]>('/social/run', null, {
      params: { publish, force },
    })
    return res.data
  }

  /** Render a single match (used to (re)generate a specific poster). */
  async generateMatch(matchId: string, publish = false, force = true): Promise<SocialRunResult> {
    const res = await http.post<SocialRunResult>(`/social/run/${matchId}`, null, {
      params: { publish, force },
    })
    return res.data
  }

  /** The approval queue: recently generated posts. */
  async listPosts(): Promise<SocialPost[]> {
    const res = await http.get<SocialPost[]>('/social/posts')
    return res.data
  }

  async updateCaptions(
    matchId: string,
    captions: { instagram?: string; facebook?: string; linkedin?: string },
  ): Promise<SocialPost> {
    const res = await http.patch<SocialPost>(`/social/posts/${matchId}/captions`, captions)
    return res.data
  }

  /** Approve & publish now. */
  async publish(matchId: string): Promise<SocialPost> {
    const res = await http.post<SocialPost>(`/social/posts/${matchId}/publish`)
    return res.data
  }

  async skip(matchId: string): Promise<SocialPost> {
    const res = await http.post<SocialPost>(`/social/posts/${matchId}/skip`)
    return res.data
  }
}

const socialService = new SocialService()
export default socialService
